/*******************************************************************************
 * resolveSentryProduct */
/**
 * Maps the current page's location to a product slug used as the Sentry
 * `product` tag.
 *
 * Sentry runs from the single shared CSMCommonWeb bundle, which has no idea
 * which product it is embedded in, so the product is derived from the URL at
 * runtime. This keeps everything in one Sentry project while still letting
 * errors be filtered/alerted per product.
 *
 * Two cases are not pure hostname lookups:
 * - EventTracker is an IIS virtual app under the ASBWorks host, so it shares
 *   ws0.asbworks.com and
 * is only distinguishable by its /et path — it must be checked before the
 *   ASBWorks host match.
 * - Admin and CommunityWebstore both live on graystep.com; admin is the admin.*
 *   subdomain, every
 *   other graystep.com subdomain is a community store.
 *
 * Returns 'unknown' for unmapped hosts (e.g. local .loc dev domains) so events
 * still flow.
 *******************************************************************************/
export function resolveSentryProduct() {
  /*----------------------------------------------------------------------*/
  /* Matches a url string and splits it into groups.                      */
  /*                                                                      */
  /* Group 1: ([^.]+) Matches all characters except for '.'               */
  /*   This will match the subdomain ('ws0' in 'ws0.asbworks.com').       */
  /*                                                                      */
  /* Group 2: ([^.]+) Matches all characters except for '.'               */
  /*   This will match the domain ('asbworks' in 'ws0.asbworks.com')      */
  /*                                                                      */
  /* Group 3: ([^.\/]+) Matches all characters except for '.' or '/'      */
  /*   This will match the TLD ('com' in 'ws0.asbworks.com')              */
  /*                                                                      */
  /* Group 4: ([^\/]+) Matches all characters except for '/'              */
  /*   This will match the first section of the path ('et' in             */
  /*   'ws0.asbworks.com/et/(S(gcbhxsaxn4yffeg5rjqiccbs))/Index.aspx' or  */
  /*   '(S(gcbhxsaxn4yffeg5rjqiccbs))' in                                 */
  /*   'ws0.asbworks.com/(S(gcbhxsaxn4yffeg5rjqiccbs))/client/apps/pos'). */
  /*----------------------------------------------------------------------*/
  const hostRegex = /^https?:\/\/([^.]+)\.([^.]+)\.([^.\/]+)\/([^\/]+).*?$/i;
  const match = window.location.href.match(hostRegex);

  const DOMAIN_ASB = 'asbworks';
  const DOMAIN_BOOSTER = 'boosterfinance';
  const DOMAIN_PTA = 'myptez';
  const DOMAIN_STORE_ASB = 'myschoolcentral';
  const DOMAIN_STORE_BOOSTER = 'myboostercentral';
  const DOMAIN_STORE_PTA = 'myptezcentral';
  const DOMAIN_GRAYSTEP = 'graystep';
  const DOMAIN_POLLING = 'pollingworks';

  /*-----------------------------------------------*/
  /* Ideally we should just return the original    */
  /* host name. The only exceptions would be for   */
  /* event tracker, admin, and community webstore. */
  /*-----------------------------------------------*/
  const PRODUCT_ET = 'eventtrackker';
  const PRODUCT_PTA = 'ptaez';
  const PRODUCT_STORE_ASB = 'asbstore';
  const PRODUCT_STORE_BOOSTER = 'boosterstore';
  const PRODUCT_STORE_PTA = 'ptastore';
  const PRODUCT_STORE_COMMUNITY = 'communitywebstore';
  const PRODUCT_ADMIN = 'csmadmin';

  if (match) {
    /*-----------------------------------------------*/
    /* Besides checking if this is Admin, we could   */
    /* use the subdomain to know what store we got   */
    /* the error from for grouping if sentry doesn't */
    /* already handle that based on subdomain.       */
    /*-----------------------------------------------*/
    const subdomain = match[1];
    const domain = match[2];

    /*----------------------------------------------------------*/
    /* The Top-Level Doamin (TLD) could be used if we wanted    */
    /* to differentiate between local, staging, and production. */
    /*----------------------------------------------------------*/
    const tld = match[3];

    /*--------------------------------------------------------------------*/
    /* Group 4 will be the first part of the path. That could be 'et' for */
    /* EventTracker, or it could be the first path to the app ('books'    */
    /* in '/books/client/apps'). Until the session token is removed,      */
    /* this group will hold the session token if not for EventTracker.    */
    /*--------------------------------------------------------------------*/
    const isET = match[4] == 'et';
    const isAdmin = subdomain == 'admin';

    switch (domain) {
      case DOMAIN_ASB:
        return isET ? PRODUCT_ET : DOMAIN_ASB;
      case DOMAIN_BOOSTER:
        return DOMAIN_BOOSTER;
      case DOMAIN_PTA:
        return PRODUCT_PTA;
      case DOMAIN_POLLING:
        return DOMAIN_POLLING;
      case DOMAIN_GRAYSTEP:
        return isAdmin ? PRODUCT_ADMIN : PRODUCT_STORE_COMMUNITY;
      case DOMAIN_STORE_ASB:
        return PRODUCT_STORE_ASB;
      case DOMAIN_STORE_BOOSTER:
        return PRODUCT_STORE_BOOSTER;
      case DOMAIN_STORE_PTA:
        return PRODUCT_STORE_PTA;
      default:
        return 'unknown';
    }
  }
}
